import { Editor, Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { Plugin } from 'prosemirror-state';
import { Slice, Fragment } from 'prosemirror-model';

const UnitLatexPasteFix = Extension.create({
	name: 'unitLatexPasteFix',
	addProseMirrorPlugins() {
		return [
			new Plugin({
				props: {
					transformPasted(slice) {
						try {
							return transformSliceForInlineMathLike(slice);
						} catch (e) {
							return slice;
						}
					},
				},
			}),
		];
	},
});

let editor = null;
let hasUnsavedChanges = false;
let beforeUnloadInstalled = false;
let skipBeforeUnloadOnce = false;

let autosaveTimer = null;
let autosaveEndpoint = '';
let autosaveInFlight = false;
let autosaveQueued = false;
let lastSavedHtml = '';
let lastKnownHtml = '';
let autosaveSeq = 0;

export function initTiptapEditor(elementId, initialHtml, uploadEndpoint, questionId, autosaveEndpointOverride) {
	const statusIcon = document.getElementById('save-status');
	const holderEl = document.getElementById(elementId);
	if (!holderEl) return null;

	autosaveEndpoint = String(autosaveEndpointOverride || '').trim();
	if (!autosaveEndpoint) {
		// default: same presenter action + signal
		try {
			autosaveEndpoint = window.location.pathname + '?do=autosaveAnswer&questionId=' + String(questionId);
		} catch (e) {
			autosaveEndpoint = '';
		}
	}

	const { saveForm, contentField } = resolveSaveTargets(holderEl);
	const syncContentField = () => {
		try {
			if (contentField) contentField.value = editor ? editor.getHTML() : '';
		} catch (e) {}
	};

	// Clean up previous instance if any
	try {
		if (editor) editor.destroy();
	} catch (e) {}
	editor = null;

	const normalizedInitialHtml = normalizeInitialHtml(initialHtml);

	editor = new Editor({
		element: holderEl,
		content: normalizedInitialHtml,
		extensions: [
			StarterKit.configure({
				// Keep it compatible with current UI expectations
				heading: { levels: [1, 2, 3, 4, 5, 6] },
				codeBlock: true,
			}),
			Underline,
			Highlight,
			Subscript,
			Superscript,
			Link.configure({
				openOnClick: false,
				autolink: true,
				linkOnPaste: true,
				HTMLAttributes: {
					rel: 'noopener noreferrer',
				},
			}),
			Image.configure({
				inline: false,
				allowBase64: false,
			}),
			Table.configure({ resizable: true }),
			TableRow,
			TableHeader,
			TableCell,
			UnitLatexPasteFix,
			Placeholder.configure({
				placeholder: 'Začněte psát odpověď…',
			}),
		],
		editorProps: {
			attributes: {
				class: 'tiptap',
				autocapitalize: 'off',
				autocorrect: 'off',
				spellcheck: 'true',
			},
		},
		onUpdate: () => {
			try {
				lastKnownHtml = editor ? editor.getHTML() : '';
			} catch (e) {
				lastKnownHtml = '';
			}
			hasUnsavedChanges = true;
			setDirtyState(statusIcon);
			syncContentField();
			scheduleAutosave(statusIcon, questionId);
		},
	});

	// Ensure field is initialized even without edits
	syncContentField();
	try {
		lastKnownHtml = editor ? editor.getHTML() : '';
		lastSavedHtml = lastKnownHtml;
	} catch (e) {
		lastKnownHtml = '';
		lastSavedHtml = '';
	}

	bindToolbar(editor, { uploadEndpoint, questionId });
	bindSaveForm(editor, statusIcon);
	installBeforeUnloadGuard();

	// Backwards compatibility: some templates/scripts expect this
	window.editorInstance = editor;

	return editor;
}

function transformSliceForInlineMathLike(slice) {
	if (!(slice instanceof Slice)) return slice;
	const mapped = mapFragmentInlineMathLike(slice.content);
	if (mapped === slice.content) return slice;
	return new Slice(mapped, slice.openStart, slice.openEnd);
}

function mapFragmentInlineMathLike(fragment) {
	let changed = false;
	const nodes = [];
	const flushTextRun = (runNodes, runText) => {
		if (!runNodes.length) return;
		const schema = runNodes[0].type.schema;
		const baseMarks = runNodes[0].marks || [];
		const { nodes: outNodes, changed: runChanged } = replaceStringWithInlineMathLikeNodes(runText, schema, baseMarks);
		if (!runChanged) {
			for (const n of runNodes) nodes.push(n);
			return;
		}
		changed = true;
		for (const n of outNodes) nodes.push(n);
	};

	let runNodes = [];
	let runText = '';

	fragment.forEach((child) => {
		if (child.isText) {
			runNodes.push(child);
			runText += String(child.text || '');
			return;
		}

		flushTextRun(runNodes, runText);
		runNodes = [];
		runText = '';

		if (child.content && child.content.size) {
			const newContent = mapFragmentInlineMathLike(child.content);
			if (newContent !== child.content) {
				changed = true;
				nodes.push(child.copy(newContent));
				return;
			}
		}

		nodes.push(child);
	});

	flushTextRun(runNodes, runText);
	return changed ? Fragment.fromArray(nodes) : fragment;
}

function replaceStringWithInlineMathLikeNodes(text, schema, marks) {
	const s = String(text || '');
	if (!s.includes('$')) return { nodes: [schema.text(s, marks)], changed: false };

	const parts = s.split(/(\$[^$\n\r]{1,300}\$)/g);
	if (parts.length === 1) return { nodes: [schema.text(s, marks)], changed: false };

	const out = [];
	let changed = false;
	for (const part of parts) {
		if (!part) continue;
		if (!part.startsWith('$') || !part.endsWith('$')) {
			out.push(schema.text(part, marks));
			continue;
		}

		const inner = part.slice(1, -1);
		const replaced = inlineMathLikeToNodes(inner, schema, marks);
		if (replaced) {
			changed = true;
			for (const n of replaced) out.push(n);
		} else {
			out.push(schema.text(part, marks));
		}
	}

	return { nodes: out.length ? out : [schema.text(s, marks)], changed };
}

function inlineMathLikeToNodes(inner, schema, baseMarks) {
	const raw = String(inner ?? '').trim();
	if (!raw) return null;

	// Greek commands / simple LaTeX expressions
	if (raw.includes('\\')) {
		const converted = convertLatexSimpleExpressionToUnicode(raw);
		if (!converted) return null;
		// If the converted expression still contains subscripts, convert them to real <sub> marks.
		if (converted.includes('_')) {
			const nodes = convertLatexExpressionWithSubscriptsToNodes(converted, schema, baseMarks);
			if (nodes) return nodes;
		}
		return [schema.text(converted, baseMarks)];
	}

	// Units: m^2 / m^3 (including common prefixes)
	const unitPowerRe = /(?:\b|\/)(?:[kcmd]?m)\s*\^\s*(?:2|3)\b/i;
	if (unitPowerRe.test(raw)) {
		let out = raw;
		out = out.replace(/\^\s*2\b/g, '²');
		out = out.replace(/\^\s*3\b/g, '³');
		return out === raw ? null : [schema.text(out, baseMarks)];
	}

	// Simple subscript like R_a or R_{a} or Ra_{12}
	const m = raw.match(/^(.+?)_\{([^{}\n\r]+)\}$|^(.+?)_([A-Za-z0-9+\-=()])$/);
	const left = m ? (m[1] != null ? m[1] : m[3]) : null;
	const sub = m ? (m[2] != null ? m[2] : m[4]) : null;
	if (left != null && sub != null) {
		const subMark = schema.marks && schema.marks.subscript ? schema.marks.subscript.create() : null;
		if (!subMark) return null;
		// Keep it conservative: only allow short subscripts (numbers/letters and a few symbols)
		const subStr = String(sub);
		if (!/^[A-Za-z0-9+\-=()]{1,12}$/.test(subStr)) return null;
		return [
			schema.text(String(left), baseMarks),
			schema.text(subStr, [...baseMarks, subMark]),
		];
	}

	// Simple expression containing subscripts somewhere inside, e.g. "K = F_g / m"
	if (raw.includes('_')) {
		const nodes = convertLatexExpressionWithSubscriptsToNodes(raw, schema, baseMarks);
		if (nodes) return nodes;
	}

	// Plain single token wrapped in $...$, e.g. "$B$" -> "B"
	// Keep conservative: only letters/numbers, no operators.
	if (/^[\p{L}\p{N}]+$/u.test(raw)) {
		return [schema.text(raw, baseMarks)];
	}

	return null;
}

function convertLatexExpressionWithSubscriptsToNodes(raw, schema, baseMarks) {
	try {
		const subMark = schema.marks && schema.marks.subscript ? schema.marks.subscript.create() : null;
		if (!subMark) return null;

		// Conservative: only allow a limited character set + subscript markers.
		// Braces are only allowed as part of _{...}.
		if (/[^{\p{L}\p{N}\s=+\-*/().,;:_<>}≤≥≠≈→←·×±≫≪]/u.test(raw)) return null;
		// Disallow backslash commands here.
		if (raw.includes('\\')) return null;

		const nodes = [];
		let buffer = '';
		let i = 0;
		let changed = false;

		function flushBuffer() {
			if (!buffer) return;
			nodes.push(schema.text(buffer, baseMarks));
			buffer = '';
		}

		while (i < raw.length) {
			const ch = raw[i];
			if (ch !== '_') {
				buffer += ch;
				i++;
				continue;
			}

			// We found a subscript marker
			flushBuffer();
			i++;
			if (i >= raw.length) return null;

			let sub = '';
			if (raw[i] === '{') {
				i++;
				const start = i;
				// No nesting; find first closing brace
				const end = raw.indexOf('}', start);
				if (end === -1) return null;
				sub = raw.slice(start, end);
				i = end + 1;
			} else {
				sub = raw[i];
				i++;
			}

			sub = String(sub).trim();
			if (!sub) return null;
			if (sub.length > 12) return null;
			if (!/^[\p{L}\p{N}+\-=()]+$/u.test(sub)) return null;

			nodes.push(schema.text(sub, [...baseMarks, subMark]));
			changed = true;
		}

		flushBuffer();
		return changed ? nodes : null;
	} catch (e) {
		return null;
	}
}

function convertLatexSimpleExpressionToUnicode(rawInner) {
	try {
		const s = normalizeSpacedLatex(String(rawInner ?? '').trim());
		if (!s) return null;

		// Too risky / complex LaTeX: keep as-is
		if (/[{}]/.test(s)) return null;
		if (/\\(?:frac|begin|end|left|right|displaystyle|text|mathrm|mathbf|mathit|mathcal|overline|underline|sqrt)\b/i.test(s)) {
			return null;
		}

		// Replace greek letters + a small set of safe math symbols.
		// If expression contains any unknown \command, do not convert.
		const cmdRe = /\\([A-Za-z]+)\b/g;
		let unknown = false;
		const commands = new Set();
		let m;
		while ((m = cmdRe.exec(s))) {
			commands.add(m[1]);
		}

		const map = getLatexCommandMap();
		for (const cmd of commands) {
			if (!Object.prototype.hasOwnProperty.call(map, cmd)) {
				unknown = true;
				break;
			}
		}
		if (unknown) return null;

		let out = s;
		// Remove simple LaTeX spacing commands like \, \; \: \!
		out = out.replace(/\\[,;:!]/g, '');
		out = out.replace(cmdRe, (_, name) => map[name] ?? _);

		// Common escaped characters
		out = out.replace(/\\_/g, '_');
		out = out.replace(/\\%/g, '%');
		out = out.replace(/\\#/g, '#');
		out = out.replace(/\\&/g, '&');
		out = out.replace(/\\\s/g, ' '); // "\ " -> space

		// Normalize whitespace a bit
		out = out.replace(/\s{2,}/g, ' ').trim();

		return out && out !== s ? out : null;
	} catch (e) {
		return null;
	}
}

function normalizeSpacedLatex(input) {
	let s = String(input ?? '');
	if (!s) return s;

	// Some sources paste TeX with spaces between command letters: "\\ m u _ r".
	// Collapse spaced commands like "\\ m u" -> "\\mu" (requires at least 2 letters).
	s = s.replace(/\\(?:\s*[A-Za-z]){2,}/g, (m) => {
		return '\\' + m.replace(/^\\/, '').replace(/\s+/g, '');
	});

	// Normalize spacing around _ and ^
	s = s.replace(/\s*_\s*/g, '_');
	s = s.replace(/\s*\^\s*/g, '^');

	// Trim internal excessive whitespace
	s = s.replace(/\s{2,}/g, ' ').trim();
	return s;
}

function getLatexCommandMap() {
	// Greek map + small set of safe math symbols.
	// Note: we intentionally map both epsilon variants to ε for readability.
	return {
		qquad: ' ',
		quad: ' ',
		// math operators/symbols
		cdot: '·',
		times: '×',
		pm: '±',
		le: '≤',
		leq: '≤',
		ge: '≥',
		geq: '≥',
		neq: '≠',
		approx: '≈',
		gg: '≫',
		ll: '≪',
		to: '→',
		rightarrow: '→',
		leftarrow: '←',
		// greek letters (lowercase)
		alpha: 'α',
		beta: 'β',
		gamma: 'γ',
		delta: 'δ',
		epsilon: 'ε',
		varepsilon: 'ε',
		zeta: 'ζ',
		eta: 'η',
		theta: 'θ',
		vartheta: 'θ',
		iota: 'ι',
		kappa: 'κ',
		lambda: 'λ',
		mu: 'μ',
		nu: 'ν',
		xi: 'ξ',
		omicron: 'ο',
		pi: 'π',
		varpi: 'π',
		rho: 'ρ',
		varrho: 'ρ',
		sigma: 'σ',
		varsigma: 'ς',
		tau: 'τ',
		upsilon: 'υ',
		phi: 'φ',
		varphi: 'φ',
		chi: 'χ',
		psi: 'ψ',
		omega: 'ω',
		// greek letters (uppercase)
		Alpha: 'Α',
		Beta: 'Β',
		Gamma: 'Γ',
		Delta: 'Δ',
		Epsilon: 'Ε',
		Zeta: 'Ζ',
		Eta: 'Η',
		Theta: 'Θ',
		Iota: 'Ι',
		Kappa: 'Κ',
		Lambda: 'Λ',
		Mu: 'Μ',
		Nu: 'Ν',
		Xi: 'Ξ',
		Omicron: 'Ο',
		Pi: 'Π',
		Rho: 'Ρ',
		Sigma: 'Σ',
		Tau: 'Τ',
		Upsilon: 'Υ',
		Phi: 'Φ',
		Chi: 'Χ',
		Psi: 'Ψ',
		Omega: 'Ω',
	};
}

function convertLatexGreekToUnicode(rawInner) {
	try {
		const s = String(rawInner ?? '').trim();
		// Only allow a single command token like "\Phi" with optional surrounding whitespace.
		const m = s.match(/^\\([A-Za-z]+)$/);
		if (!m) return null;
		const cmd = m[1];

		const map = {
			// Lowercase
			alpha: 'α',
			beta: 'β',
			gamma: 'γ',
			delta: 'δ',
			epsilon: 'ε',
			zeta: 'ζ',
			eta: 'η',
			theta: 'θ',
			iota: 'ι',
			kappa: 'κ',
			lambda: 'λ',
			mu: 'μ',
			nu: 'ν',
			xi: 'ξ',
			omicron: 'ο',
			pi: 'π',
			rho: 'ρ',
			sigma: 'σ',
			tau: 'τ',
			upsilon: 'υ',
			phi: 'φ',
			chi: 'χ',
			psi: 'ψ',
			omega: 'ω',
			// Variants
			varepsilon: 'ϵ',
			vartheta: 'ϑ',
			varpi: 'ϖ',
			varrho: 'ϱ',
			varsigma: 'ς',
			varphi: 'ϕ',
			// Uppercase
			Alpha: 'Α',
			Beta: 'Β',
			Gamma: 'Γ',
			Delta: 'Δ',
			Epsilon: 'Ε',
			Zeta: 'Ζ',
			Eta: 'Η',
			Theta: 'Θ',
			Iota: 'Ι',
			Kappa: 'Κ',
			Lambda: 'Λ',
			Mu: 'Μ',
			Nu: 'Ν',
			Xi: 'Ξ',
			Omicron: 'Ο',
			Pi: 'Π',
			Rho: 'Ρ',
			Sigma: 'Σ',
			Tau: 'Τ',
			Upsilon: 'Υ',
			Phi: 'Φ',
			Chi: 'Χ',
			Psi: 'Ψ',
			Omega: 'Ω',
		};

		return map[cmd] || map[String(cmd).toLowerCase()] || null;
	} catch (e) {
		return null;
	}
}

function normalizeInitialHtml(initialHtml) {
	const s = String(initialHtml ?? '').trim();
	if (!s) return '';
	// If backend sends JSON-stringified value by mistake, try to unwrap.
	if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
		try {
			return JSON.parse(s);
		} catch (e) {
			return s;
		}
	}
	return s;
}

function setDirtyState(statusIcon) {
	if (!statusIcon) return;
	statusIcon.style.display = 'inline-block';
	statusIcon.textContent = 'edit';
	statusIcon.style.color = '#FF9559';
	statusIcon.classList.remove('saving-pulse');
}

function setSavingState(statusIcon) {
	if (!statusIcon) return;
	statusIcon.style.display = 'inline-block';
	statusIcon.textContent = 'save';
	statusIcon.style.color = '#FF9559';
	statusIcon.classList.add('saving-pulse');
}

function setSavedState(statusIcon) {
	if (!statusIcon) return;
	statusIcon.classList.remove('saving-pulse');
	statusIcon.textContent = 'check';
	statusIcon.style.color = '#28a745';
	setTimeout(() => {
		try {
			statusIcon.style.display = 'none';
		} catch (e) {}
	}, 1000);
}

function installBeforeUnloadGuard() {
	if (beforeUnloadInstalled) return;
	beforeUnloadInstalled = true;
	window.addEventListener('beforeunload', (e) => {
		if (skipBeforeUnloadOnce) {
			skipBeforeUnloadOnce = false;
			return;
		}
		if (hasUnsavedChanges) {
			e.preventDefault();
			e.returnValue = '';
		}
	});
}

function bindSaveForm(editorInstance, statusIcon) {
	const targets = resolveSaveTargets(null);
	const form = targets.saveForm;
	const contentField = targets.contentField;
	if (!form || !contentField) return;

	// Ensure field is initialized even if user saves without edits
	try {
		contentField.value = editorInstance.getHTML();
	} catch (e) {}

	form.addEventListener('submit', (e) => {
		try {
			const html = editorInstance.getHTML();
			contentField.value = html;

			const debugSave = isDebugSaveEnabled(form);
			if (debugSave) {
				e.preventDefault();
				showDebugSave(html);
				return;
			}
			// Při ručním submitu nechceme zobrazovat beforeunload prompt.
			skipBeforeUnloadOnce = true;
			setSavingState(statusIcon);
		} catch (e) {}
	});
}

function scheduleAutosave(statusIcon, questionId) {
	try {
		if (!autosaveEndpoint) return;
		if (autosaveTimer) {
			clearTimeout(autosaveTimer);
			autosaveTimer = null;
		}
		autosaveTimer = setTimeout(() => {
			autosaveTimer = null;
			runAutosave(statusIcon, questionId);
		}, 1200);
	} catch (e) {}
}

async function runAutosave(statusIcon, questionId) {
	try {
		if (!autosaveEndpoint) return;
		let html = '';
		try {
			html = editor ? editor.getHTML() : '';
		} catch (e) {
			html = '';
		}
		lastKnownHtml = html;

		// Nothing new to save
		if (String(html) === String(lastSavedHtml)) {
			hasUnsavedChanges = false;
			setSavedState(statusIcon);
			return;
		}

		if (autosaveInFlight) {
			autosaveQueued = true;
			return;
		}
		autosaveInFlight = true;
		autosaveQueued = false;
		const seq = ++autosaveSeq;
		const htmlSent = html;
		setSavingState(statusIcon);

		const body = new URLSearchParams();
		body.set('content', htmlSent);
		// redundant but handy for debugging
		body.set('questionId', String(questionId || ''));

		const response = await fetch(autosaveEndpoint, {
			method: 'POST',
			headers: {
				'X-Requested-With': 'XMLHttpRequest',
				'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
			},
			body: body.toString(),
		});

		let result = null;
		try {
			result = await response.json();
		} catch (e) {
			result = null;
		}

		autosaveInFlight = false;
		const ok = response && response.ok && result && result.success === 1;

		// If user typed meanwhile, keep dirty and try again.
		let currentHtml = '';
		try {
			currentHtml = editor ? editor.getHTML() : '';
		} catch (e) {
			currentHtml = '';
		}
		lastKnownHtml = currentHtml;

		if (ok && String(currentHtml) === String(htmlSent) && seq === autosaveSeq) {
			lastSavedHtml = htmlSent;
			hasUnsavedChanges = false;
			setSavedState(statusIcon);
		} else {
			hasUnsavedChanges = true;
			setDirtyState(statusIcon);
		}

		if (!ok) {
			// leave dirty state; don't spam alerts
			console.error('Autosave failed:', result);
		}

		if (autosaveQueued || String(lastKnownHtml) !== String(lastSavedHtml)) {
			scheduleAutosave(statusIcon, questionId);
		}
	} catch (e) {
		autosaveInFlight = false;
		hasUnsavedChanges = true;
		setDirtyState(statusIcon);
		console.error('Autosave error:', e);
	}
}

function isDebugSaveEnabled(formEl) {
	try {
		if (formEl && String(formEl.dataset.debugSave || '') === '1') return true;
		const params = new URLSearchParams(window.location.search || '');
		return params.get('debugSave') === '1';
	} catch (e) {
		return false;
	}
}

function showDebugSave(html) {
	try {
		const panel = document.getElementById('tiptap-debug-panel');
		const out = document.getElementById('tiptap-debug-output');
		const meta = document.getElementById('tiptap-debug-meta');
		if (out) out.value = String(html ?? '');
		if (meta) meta.textContent = `Délka HTML: ${String((html ?? '').length)}`;
		if (panel) panel.style.display = 'block';
		console.log('[DEBUG SAVE] HTML:', html);
	} catch (e) {}
}

function resolveSaveTargets(contextEl) {
	let saveForm = null;
	try {
		saveForm = document.querySelector('form[data-tiptap-save-form]');
	} catch (e) {}
	if (!saveForm && contextEl && contextEl.closest) {
		try {
			saveForm = contextEl.closest('form');
		} catch (e) {}
	}

	let contentField = null;
	if (saveForm) {
		contentField =
			saveForm.querySelector('[data-tiptap-content-field]') ||
			saveForm.querySelector('textarea[name="content"], input[name="content"], textarea[name$="[content]"], input[name$="[content]"]');
	}
	if (!contentField) {
		contentField = document.querySelector('[data-tiptap-content-field]');
	}

	return { saveForm, contentField };
}

function bindToolbar(editorInstance, { uploadEndpoint, questionId }) {
	if (!editorInstance) return;
	const toolbar = document.querySelector('[data-tiptap-toolbar]');
	if (!toolbar) return;

	const imageInput = ensureImageInput(toolbar);

	toolbar.addEventListener('click', (e) => {
		const btn = e.target && e.target.closest ? e.target.closest('button[data-cmd]') : null;
		if (!btn) return;
		e.preventDefault();

		const cmd = btn.dataset.cmd;
		switch (cmd) {
			case 'bold':
				editorInstance.chain().focus().toggleBold().run();
				break;
			case 'italic':
				editorInstance.chain().focus().toggleItalic().run();
				break;
			case 'underline':
				editorInstance.chain().focus().toggleUnderline().run();
				break;
			case 'strike':
				editorInstance.chain().focus().toggleStrike().run();
				break;
			case 'code':
				editorInstance.chain().focus().toggleCode().run();
				break;
			case 'highlight':
				editorInstance.chain().focus().toggleHighlight().run();
				break;
			case 'subscript':
				editorInstance.chain().focus().toggleSubscript().run();
				break;
			case 'superscript':
				editorInstance.chain().focus().toggleSuperscript().run();
				break;
			case 'paragraph':
				editorInstance.chain().focus().setParagraph().run();
				break;
			case 'h1':
				editorInstance.chain().focus().toggleHeading({ level: 1 }).run();
				break;
			case 'h2':
				editorInstance.chain().focus().toggleHeading({ level: 2 }).run();
				break;
			case 'h3':
				editorInstance.chain().focus().toggleHeading({ level: 3 }).run();
				break;
			case 'bulletList':
				editorInstance.chain().focus().toggleBulletList().run();
				break;
			case 'orderedList':
				editorInstance.chain().focus().toggleOrderedList().run();
				break;
			case 'blockquote':
				editorInstance.chain().focus().toggleBlockquote().run();
				break;
			case 'codeBlock':
				editorInstance.chain().focus().toggleCodeBlock().run();
				break;
			case 'hr':
				editorInstance.chain().focus().setHorizontalRule().run();
				break;
			case 'undo':
				editorInstance.chain().focus().undo().run();
				break;
			case 'redo':
				editorInstance.chain().focus().redo().run();
				break;
			case 'link': {
				const prevUrl = editorInstance.getAttributes('link').href;
				const url = window.prompt('Odkaz (URL):', prevUrl || '');
				if (url === null) return;
				if (!String(url).trim()) {
					editorInstance.chain().focus().extendMarkRange('link').unsetLink().run();
					return;
				}
				editorInstance.chain().focus().extendMarkRange('link').setLink({ href: String(url).trim() }).run();
				break;
			}
			case 'image':
				imageInput.click();
				break;
			case 'table':
				editorInstance.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
				break;
			default:
				break;
		}
	});

	imageInput.addEventListener('change', async () => {
		const file = imageInput.files && imageInput.files[0] ? imageInput.files[0] : null;
		imageInput.value = '';
		if (!file) return;

		try {
			const url = await uploadImage(file, uploadEndpoint, questionId);
			if (!url) throw new Error('Upload failed');
			editorInstance.chain().focus().setImage({ src: url, alt: file.name || '' }).run();
		} catch (e) {
			console.error('Upload error:', e);
			alert('Nepodařilo se nahrát obrázek: ' + (e && e.message ? e.message : 'neznámá chyba'));
		}
	});
}

function ensureImageInput(toolbarEl) {
	let input = toolbarEl.querySelector('input[type="file"][data-tiptap-image-input]');
	if (input) return input;
	input = document.createElement('input');
	input.type = 'file';
	input.accept = 'image/*';
	input.className = 'd-none';
	input.dataset.tiptapImageInput = '1';
	toolbarEl.appendChild(input);
	return input;
}

async function uploadImage(file, uploadEndpoint, questionId) {
	if (!uploadEndpoint) throw new Error('Upload endpoint missing');

	const formData = new FormData();
	formData.append('image', file);

	const response = await fetch(uploadEndpoint, {
		method: 'POST',
		body: formData,
	});

	const result = await response.json();
	if (result && result.success === 1 && result.file && result.file.url) {
		return result.file.url;
	}
	throw new Error(result && result.error ? result.error : 'Upload failed');
}
